/**
 * Adapter — MCP `Tool` → pi-ai `AgentTool`.
 *
 * Tools are prefixed by their server id so two servers can expose
 * `search` without colliding. The model sees e.g. `linear__search`;
 * `execute` strips the prefix and invokes the caller-supplied `call`
 * closure, which in practice routes through the pool + session-retry
 * helper in `index.js`. Keeping the adapter transport-agnostic means
 * it picks up freshly-handshaken transports after a session eviction
 * without re-wrapping the tool list.
 *
 * Content shape: pi-ai's AgentToolResult accepts TextContent and
 * ImageContent arrays; MCP can also return audio + embedded
 * resources. We pass text and image through unchanged and degrade
 * the rest into a text block describing the content so the loop
 * doesn't drop a successful tool call on the floor.
 */

import { jsonSchemaToTypeBox } from "./schema.js";

export function mcpToolToAgentTool({ tool, serverPrefix, call }) {
  const prefixed = serverPrefix ? `${serverPrefix}__${tool.name}` : tool.name;
  const parameters = jsonSchemaToTypeBox(
    tool.inputSchema && Object.keys(tool.inputSchema).length ? tool.inputSchema : { type: "object", properties: {} },
  );
  const description = composeDescription(tool, serverPrefix);
  return {
    name: prefixed,
    label: tool.title || tool.name,
    description,
    parameters,
    async execute(_toolCallId, params, signal) {
      let result;
      try {
        result = await call(tool.name, params ?? {}, { signal });
      } catch (err) {
        return {
          content: [{ type: "text", text: `MCP error: ${err.message || String(err)}` }],
        };
      }
      const content = normalizeContent(result?.content);
      const details = result?.structuredContent ?? result ?? null;
      // MCP carries tool-level errors in `isError` alongside content
      // — surface that as a text marker so the loop can show it as a
      // failure rather than silently treating it as success.
      if (result?.isError) {
        content.unshift({ type: "text", text: "[tool returned isError=true]" });
      }
      return { content, details };
    },
  };
}

function composeDescription(tool, serverPrefix) {
  const parts = [];
  if (serverPrefix) parts.push(`[${serverPrefix}]`);
  if (tool.description) parts.push(tool.description.trim());
  else parts.push(`${tool.name} (MCP tool)`);
  return parts.join(" ");
}

function normalizeContent(contentIn) {
  const out = [];
  if (!Array.isArray(contentIn)) return out;
  for (const c of contentIn) {
    if (!c || typeof c !== "object") continue;
    if (c.type === "text" && typeof c.text === "string") {
      out.push({ type: "text", text: c.text });
    } else if (c.type === "image" && typeof c.data === "string" && typeof c.mimeType === "string") {
      out.push({ type: "image", data: c.data, mimeType: c.mimeType });
    } else if (c.type === "resource" && c.resource) {
      // Inline resource: surface text contents directly so the model
      // can reason about them; binary resources get a stub line.
      const r = c.resource;
      if (typeof r.text === "string") {
        out.push({ type: "text", text: r.text });
      } else if (typeof r.uri === "string") {
        out.push({ type: "text", text: `[resource: ${r.uri}]` });
      }
    } else if (c.type === "audio") {
      out.push({ type: "text", text: "[audio content omitted]" });
    } else {
      // Unknown content type — preserve as JSON so we don't silently lose data.
      out.push({ type: "text", text: `[${c.type ?? "unknown"} content] ${safeStringify(c)}` });
    }
  }
  return out;
}

function safeStringify(value) {
  try { return JSON.stringify(value); } catch { return String(value); }
}
